import { useCallback, useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { useWorldCupMatchesQuery } from '../../../src/worldcup/hooks/useWorldCupMatchesQuery';
import { useCompetitionsListQuery } from '../../../src/competitions/hooks/useCompetitionsListQuery';
import { buildTournamentKnockoutBracket } from '../../../src/competitions/helpers/buildTournamentKnockoutBracket.helper';
import { competitionBasePath, competitionMatchRoute } from '../../../src/competitions/helpers/competitionRoutes.helper';
import { TournamentBracketView } from './TournamentBracketView';

/**
 * Bracket container (specs/champions-league, Story 6).
 *
 * **Revised 2026-09-05 (AC-6.6, AC-6.8).** This used to share the picks screen's whole write
 * path — the debounced saver and the boost hook — because the bracket rendered editable pick
 * cards. It is now the mirrored tree the World Cup uses, and a tree is reference: it reads the
 * matches query and writes nothing. Knockout fixtures are picked in the Picks tab, so a pick
 * keeps one home rather than two that have to be kept in step.
 */
export function TournamentBracketScreen() {
  const { competitionId: rawCompetitionId } = useLocalSearchParams<{ competitionId: string }>();
  const competitionId = rawCompetitionId ?? '';

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useWorldCupMatchesQuery(competitionId, competitionId !== '');

  // The matches query is a static/me pair behind one hook and exposes no `refetch`, so Retry
  // invalidates both keys — the same thing the picks screens do.
  const onRetry = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.matchesStatic(competitionId) });
    queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.matchesMe(competitionId) });
  }, [queryClient, competitionId]);
  const { data: competitions } = useCompetitionsListQuery();
  const competition = competitions?.find((c) => c.id === competitionId);

  const model = useMemo(() => {
    const basePath = competitionBasePath(competitionId);
    return buildTournamentKnockoutBracket(
      data?.matches ?? [],
      competition?.rules?.stageOrder ?? [],
      competition?.rules?.multiRoundStages ?? {},
      (match) => competitionMatchRoute(basePath, match.matchId),
    );
  }, [
    data?.matches,
    competition?.rules?.stageOrder,
    competition?.rules?.multiRoundStages,
    competitionId,
  ]);

  return (
    <TournamentBracketView
      model={model}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
    />
  );
}
